import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LabResultsTable, { type LabResult } from '@/components/LabResultsTable';
import { ClinicalDataLanguageNotice } from '@/components/ClinicalDataLanguageNotice';
import type { StartupPayload } from '@/types/startup';
import ExplainButton from './ExplainButton';
import LabPanels from './LabPanels';
import { formatRecordDateTime } from './records-utils';

interface LabsSectionProps {
  labResults: LabResult[];
  /** Watched ids and statuses from the startup payload; null renders the full table. */
  labs?: StartupPayload['sections']['labs'] | null;
  patientFirstName: string;
  onAskQuestion: (question: string) => void;
  variant: 'main' | 'rail';
}

const STATUS_LABEL_KEYS: Record<string, string> = {
  normal: 'records.statusNormal',
  low: 'records.statusLow',
  high: 'records.statusHigh',
  critical: 'records.statusCritical',
};

const STATUS_BADGE_CLASSES: Record<string, string> = {
  normal: 'text-gray-600 bg-gray-100',
  low: 'text-amber-700 bg-amber-50',
  high: 'text-amber-700 bg-amber-50',
  critical: 'text-red-700 bg-red-50',
};

export default function LabsSection({ labResults, labs, patientFirstName, onAskQuestion, variant }: LabsSectionProps) {
  const { t, i18n } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const rowPadding = variant === 'rail' ? 'px-3' : 'px-4';

  // Startup statuses override the record's interpretation; values, units, ranges and dates stay the record's.
  const merged: LabResult[] = (labResults || []).map((lab) => ({ ...lab, status: labs?.status[lab.id] ?? lab.status }));
  const watched = labs
    ? labs.watchedLabIds.map((id) => merged.find((lab) => lab.id === id)).filter((lab): lab is LabResult => !!lab)
    : [];

  const statusLabel = (status: string) => {
    const key = STATUS_LABEL_KEYS[(status || '').toLowerCase()];
    return key ? t(key) : status;
  };

  // Startup off or failed: the legacy table. Startup ran but flagged nothing
  // to watch: go straight to the grouped panels rather than the old table.
  if (!labs) {
    return <LabResultsTable labResults={merged} onAskQuestion={onAskQuestion} />;
  }
  if (watched.length === 0) {
    return (
      <div>
        <ClinicalDataLanguageNotice />
        <LabPanels labResults={merged} tests={labs.tests} statusLabel={statusLabel} onAskQuestion={onAskQuestion} variant={variant} />
      </div>
    );
  }

  const toggleButton = (
    <button
      onClick={() => setShowAll(!showAll)}
      className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-gray-300 hover:bg-gray-50 text-text-primary"
      data-testid="button-see-all-results"
    >
      {showAll ? <ChevronLeft className="h-4 w-4" /> : null}
      {showAll ? t('records.backToWatchedResults') : t('records.seeAllResults')}
      {showAll ? null : <ChevronRight className="h-4 w-4" />}
    </button>
  );

  if (showAll) {
    return (
      <div>
        <ClinicalDataLanguageNotice />
        <div className="mb-3">{toggleButton}</div>
        <LabPanels labResults={merged} tests={labs?.tests} statusLabel={statusLabel} onAskQuestion={onAskQuestion} variant={variant} />
      </div>
    );
  }

  const otherCount = merged.length - watched.length;

  return (
    <div>
      <ClinicalDataLanguageNotice />
      <div className="mb-2">
        <h3 className="text-sm font-semibold text-text-primary">{t('records.watchedLabsHeading')}</h3>
        <p className="text-sm text-text-secondary">{t('records.selectedByAi', { name: patientFirstName })}</p>
      </div>
      <div className="rounded-lg border bg-white divide-y">
        {watched.map((lab) => {
          const status = (lab.status || '').toLowerCase();
          const result = `${lab.value}${lab.unit ? ` ${lab.unit}` : ''}`;
          return (
            <div key={lab.id} className={`flex items-start gap-3 ${rowPadding} py-2.5`} data-testid={`row-lab-${lab.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-text-primary">{lab.name}</p>
                <div className="flex items-center gap-2 flex-wrap mt-0.5">
                  <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_BADGE_CLASSES[status] || STATUS_BADGE_CLASSES.normal}`}>
                    {statusLabel(lab.status)}
                  </span>
                  {lab.referenceRange && (
                    <span className="text-sm text-text-secondary">{t('records.usualRange', { range: lab.referenceRange })}</span>
                  )}
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-semibold text-text-primary leading-tight">
                  <span>{String(lab.value)}</span>
                  {lab.unit && <span className="font-normal text-text-secondary"> <span>{lab.unit}</span></span>}
                </p>
                <p className="text-xs text-text-secondary mt-0.5">{formatRecordDateTime(lab.date, i18n.resolvedLanguage || 'en')}</p>
              </div>
              <ExplainButton
                onClick={() => onAskQuestion(t('explain.labResult', { testName: lab.name, result, status: statusLabel(lab.status) }))}
                ariaLabel={t('explain.labResultAriaLabel', { testName: lab.name })}
                testId={`button-explain-test-${lab.id}`}
              />
            </div>
          );
        })}
      </div>
      {otherCount > 0 && (
        <p className="text-sm text-text-secondary mt-3">{t('records.otherResultsNotShown', { count: otherCount })}</p>
      )}
      <div className="mt-2">{toggleButton}</div>
    </div>
  );
}
